import _ from 'lodash'

/**
 * TMR Routes Provider.
 *
 * Allows modules to add/change/remove routes during the configuration phase.
 *
 * If you need to add routes based on other routes, you can register a function
 * in the `afterConfigCallbacks` array, that will be executed when all routes
 * have been added, but just before routes are converted to states.
 *
 * It is not necessary to use the provider directly. You can register a route
 * simply by exporting a `route` object and {@link ngModuleRegister} will take
 * care of the rest.
 *
 * @example <caption>Using a route export</caption>
 * let tmrSomeModuleRoute = {
 *   name: 'My Route',
 *   url: '/my_route',
 *   templateUrl: my_route.html',
 *   frontPage: true
 * };
 * export { tmrSomeModuleRoute as route };
 *
 * @example <caption>Using a config export</caption>
 * function TmrSomeModuleConfig(TmrRoutesProvider) { 'ngInject';
 *   TmrRoutesProvider.routes.push({
 *     name: 'My Route',
 *     url: '/my_route',
 *     templateUrl: 'my_route.html',
 *     frontPage: true
 *   });
 * }
 * export { TmrSomeModuleConfig as config };
 *
 * @alias TmrRoutesProvider
 * @type {ngProvider}
 * @see {@link TmrRoute} for details about the route object
 * @todo Document the extendable appState
 */
export class TmrRoutes {
  static ngType = 'provider'

  constructor($stateProvider, $urlServiceProvider) {
    'ngInject'
    Object.assign(this, {
      $stateProvider,
      $urlServiceProvider,
      routes: [],
      afterConfigCallbacks: [],
      appState: {},
    })
  }

  /**
   * Routes factory.
   */
  $get($rootScope, $urlService, UserStorage, TmrInvalidConfigurationError) {
    'ngInject'
    return new TmrRoutesService(
      $rootScope,
      $urlService,
      UserStorage,
      TmrInvalidConfigurationError,
      this.routes
    )
  }

  /**
   * Completes the setup phase converting TMR routes to UI Router states.
   */
  setupRoutes(rootAppState) {
    // Execute after config callbacks, then clean up.
    this.afterConfigCallbacks.forEach(callback => callback(this.routes))
    this.afterConfigCallbacks.length = 0

    // Process routes.
    this.routes.forEach(route => this.processRoute(route))

    // Convert routes to states.
    this.routes.forEach(route => this.$stateProvider.state(route))

    // Add root app state.
    this.$stateProvider.state(_.merge({}, rootAppState, this.appState))
  }

  /**
   * Searches for a registered route by name.
   *
   * @param {string} name
   * @returns {TmrRoute|undefined}
   */
  getRoute(name) {
    return this.routes.find(route => route.name == name)
  }

  /**
   * Converts and adds a TMR route to the UI Router states.
   */
  processRoute(route) {
    if (route.tmrRouteProcessed) return

    this.validateRoute(route)

    // If not otherwise specified, all routes descend from the main "app" state.
    if (route.parent === undefined && route.name.indexOf('app.') !== 0) {
      route.name = 'app.' + route.name

      if (route.component) {
        if (!route.views) route.views = {}

        // If not otherwise specified, routes are full pages rendered in the "main"
        // view, which is defined in app.html.
        // ! = absolute reference to ui-view="main"
        if (!route.views['!main']) {
          route.views['!main'] = route.component
          delete route.component
        }
      }
    }

    route.tmrRouteProcessed = true
  }

  validateRoute(route) {
    // Enforce use of components.
    if (
      route.url &&
      !route.component &&
      !route.abstract &&
      (!route.views || !route.views['!main']) &&
      !route.redirectTo
    ) {
      throw new Error(`The route ${route.name} is missing a component.`)
    }

    // Enforce access control.
    if (route.mainMenu === true && route.accessControl === undefined) {
      throw new Error(
        `Route ${route.name} is in main menu but has no access control.`
      )
    }
  }
}

/**
 * Stores defined routes and the frontpage state name
 *
 * @alias TmrRoutes
 * @type {ngService}
 * @prop {array<TmrRoute>} routes
 * @prop {string} frontPage
 */
class TmrRoutesService {
  constructor(
    $rootScope,
    $urlService,
    UserStorage,
    TmrInvalidConfigurationError,
    routes
  ) {
    Object.assign(this, {
      $rootScope,
      $urlService,
      UserStorage,
      TmrInvalidConfigurationError,
    })
    this.init(routes)
  }

  /**
   * Initializes the service by setting up the `routes` and `frontPage` properties
   *
   * @param {object} routes
   */
  init(routes) {
    this.routes = Object.freeze(routes)

    // Setup default front page. This may be overridden by user role.
    let frontPage = routes.filter(route => route.frontPage)

    if (frontPage.length > 1) {
      throw new this.TmrInvalidConfigurationError('Multiple front pages')
    } else if (frontPage.length == 0) {
      throw new this.TmrInvalidConfigurationError('Missing front page')
    }

    this.frontPage = frontPage[0].name
  }

  /**
   * Initializes dynamic per-role frontPage
   */
  initFrontPage() {
    // Defer initialization of url matching system.
    this.$urlService.deferIntercept()
    // Setup user update event
    this.$rootScope.$on('beforeloggeduserupdate', (event, user) =>
      this.updateFrontPage(user)
    )
    // Trigger user update event immediately at first load.
    this.UserStorage.getUser()
      .then(user => this.updateFrontPage(user))
      .finally(() => {
        // Set dynamic frontpage
        this.$urlService.rules.initial({ state: this.frontPage })
        // Resume url matching system.
        this.$urlService.listen()
        this.$urlService.sync()
      })
  }

  /**
   * Updates dynamic per-role frontPage
   */
  updateFrontPage(user) {
    // Load user and check her roles.
    let role = user
      .$getRoles()
      .find(role => role.data && JSON.parse(role.data).frontPage)
    let routeName = role ? JSON.parse(role.data).frontPage : this.frontPage
    if (this.UserStorage.access(routeName)) this.frontPage = routeName
  }
}

/**
 * Initializes {@link TmrRoutes} at runtime
 *
 * @type {ngRun}
 */
let tmrRoutesServiceRun = /*@ngInject*/ TmrRoutes => TmrRoutes.initFrontPage()

export { tmrRoutesServiceRun as run }
